import { Lock, Pencil } from "lucide-react";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import { Button } from "../../components/primitives";
import { diffWords, markProtected, type DiffSegment } from "../../lib/diff";

/**
 * PLAN DIFF — the before/after on /new.
 *
 * This is components/DocumentDiff.tsx with four defects fixed. All four were measured on the
 * live app; none of them is a matter of taste.
 *
 *  1. THE PROPOSED TEXT WAS PRINTED TWICE PER CARD — once as the marked-up diff, then again,
 *     raw, in an always-open <textarea rows={4}>. The second copy was the one that shipped,
 *     and it CLIPPED: scrollHeight 216/240/192 against clientHeight 120 on all three cards.
 *     Half the text an operator was about to send to a funder was hidden inside a scroll box,
 *     on the one surface whose entire job is to show them what they are approving. The
 *     duplicate is gone. The rewrite is on demand: "Rewrite this text" swaps the After block
 *     in place for an AutoGrowTextarea that grows to its content and therefore cannot clip.
 *
 *  2. THE MARKS SHREDDED THE SENTENCE. `ins::before { content: "+" }` fired on EVERY fragment,
 *     so one sentence read "+ For Wellcome Trust, ATLAS + should open as a + governed medical
 *     AI evaluation + initiative:". Worse, `del { text-decoration: line-through }` struck
 *     through its own "−" marker, which renders it as an EQUALS SIGN — the non-colour channel
 *     meant to say "removed" was drawing a glyph that reads "unchanged". There is now ONE
 *     mark per BLOCK, carried in the block's label. Within the prose, the second channel is
 *     strikethrough on <del> and weight on <ins>: both survive greyscale, neither litters.
 *
 *  3. THE CARD-IN-CARD. Each side was a bordered, rounded, tinted box inside a bordered card
 *     inside a bordered panel inside a panel. At 390px that left the document body 232px wide
 *     — 29 characters of Source Serif 4 — while 126px of the phone (32% of the screen) was
 *     spent on padding and borders carrying zero information. The sides are now two regions
 *     separated by one hairline. Nothing else changes and the measure grows by ~90px.
 *
 *  4. A PROTECTED URL COULD NOT WRAP. `.doc-protected` set `white-space: nowrap`, so a long
 *     protected token pushed the card off a 390px screen. It wraps now, and stays visually
 *     SEALED across the wrap via box-decoration-break, so it can never read as two facts.
 *
 * NOTE FOR CONSOLIDATION: all four fixes belong in the shared DocumentDiff primitive so that
 * /review gets them too — /new and /review must never diverge on the single most important
 * comprehension task in the product. This module exists only because the primitives layer is
 * owned by another workstream this round.
 */

/** Split a run of text into protected / unprotected pieces and seal the protected ones. */
function ProtectedText({ value }: { value: string }) {
  const pieces = markProtected(value);
  return (
    <>
      {pieces.map((piece, index) =>
        piece.protected ? (
          <mark className="plan-protected" title="Protected fact — never altered" key={index}>
            {piece.value}
          </mark>
        ) : (
          <Fragment key={index}>{piece.value}</Fragment>
        )
      )}
    </>
  );
}

function DiffRun({ segments, side }: { segments: DiffSegment[]; side: "before" | "after" }) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "equal") return <ProtectedText value={segment.value} key={index} />;
        // Each side shows only its OWN change, so each column reads as continuous prose
        // rather than as a merge conflict.
        if (side === "before" && segment.type === "delete") {
          return (
            <del key={index}>
              <ProtectedText value={segment.value} />
            </del>
          );
        }
        if (side === "after" && segment.type === "insert") {
          return (
            <ins key={index}>
              <ProtectedText value={segment.value} />
            </ins>
          );
        }
        return null;
      })}
    </>
  );
}

/**
 * The rewrite surface. MULTI-LINE, and it grows to its content, so the copy that actually
 * ships can never be clipped. AutoGrowInput (the primitive) suppresses Enter and strips
 * newlines because it carries single-line semantics; the proposed copy is prose and may
 * legitimately need a paragraph break, so this keeps them.
 */
function AutoGrowTextarea({
  value,
  onValueChange,
  className,
  ...rest
}: {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange" | "className">) {
  const ref = useRef<HTMLTextAreaElement>(null);

  function resize(element: HTMLTextAreaElement | null) {
    if (!element) return;
    element.style.height = "auto";
    // The control is box-sizing: border-box with a 1px hairline top and bottom, but
    // scrollHeight counts only content + padding, never the border. Setting height to a bare
    // scrollHeight therefore leaves clientHeight = scrollHeight − border, so the box clips its
    // own last line by exactly the border total (measured 2px). Add the real border widths
    // back so clientHeight >= scrollHeight holds and the rewrite surface never truncates.
    const styles = window.getComputedStyle(element);
    const border =
      (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0);
    element.style.height = `${element.scrollHeight + border}px`;
  }

  useEffect(() => {
    resize(ref.current);
  }, [value]);

  return (
    <textarea
      {...rest}
      ref={ref}
      className={`plan-rewrite-input ${className || ""}`}
      rows={3}
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value);
        resize(event.currentTarget);
      }}
    />
  );
}

export function PlanDiff({
  before,
  after,
  why,
  rewritten,
  rejected,
  onAfterChange,
  editLabel,
  editTestId
}: {
  before: string;
  after: string;
  /**
   * THE REASON FOR THE EDIT IS A CHILD OF THE DIFF, NOT A ROW UNDER IT — and that is what kills
   * the last dead column on this screen.
   *
   * At >=1024 the diff is two columns. The proposed text is ALWAYS longer than the sentence it
   * replaces (it is the source sentence plus this reader's framing), so "Current text" ran short
   * and left ~110-160px of white under it in every row while "Proposed text" ran on. Sizing the
   * box to its content only removed the fence around the void; the void was still there.
   *
   * "Why" is explanatory prose ABOUT the source sentence, so it belongs beside it. Rendered as a
   * child of .plan-diff it can be placed in the LEFT column beneath "Current text", which fills
   * that column with the real content it was missing, balances both sides and shortens the row.
   * On the phone it is one column and the reading order is unchanged: current → proposed → why.
   */
  why: string;
  /** The operator has hand-edited this proposal. The label says so. */
  rewritten: boolean;
  /** Rewriting a rejected edit returns it to "no decision". Disclosed, not silent. */
  rejected: boolean;
  onAfterChange: (value: string) => void;
  editLabel: string;
  editTestId: string;
}) {
  const [editing, setEditing] = useState(false);
  const fieldId = useId();
  const segments = diffWords(before, after);
  const changed = segments.some((segment) => segment.type !== "equal");

  return (
    <div className="plan-diff" data-testid="change-diff" data-changed={changed ? "true" : "false"}>
      <div className="plan-diff-side" data-side="before">
        <span className="plan-diff-label">
          <span className="plan-diff-mark" data-mark="del" aria-hidden="true">
            −
          </span>
          Current text
        </span>
        <p className="plan-diff-body" data-testid="diff-before">
          <DiffRun segments={segments} side="before" />
        </p>
      </div>

      <div className="plan-diff-side" data-side="after">
        <span className="plan-diff-label">
          <span className="plan-diff-mark" data-mark="ins" aria-hidden="true">
            +
          </span>
          {rewritten ? "Proposed text — rewritten by you" : "Proposed text"}
        </span>

        {editing ? (
          <div className="plan-rewrite">
            <label className="plan-rewrite-label" htmlFor={fieldId}>
              Your rewrite
            </label>
            <AutoGrowTextarea
              id={fieldId}
              aria-label={editLabel}
              data-testid={editTestId}
              value={after}
              onValueChange={onAfterChange}
            />
            <p className="plan-rewrite-help">
              {rejected
                ? "Rewriting a rejected edit returns it to no decision."
                : "Your text is marked against the original, exactly like the compiler's."}
            </p>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <p className="plan-diff-body" data-testid="diff-after">
              <DiffRun segments={segments} side="after" />
            </p>
            <Button
              variant="ghost"
              size="sm"
              data-testid="edit-plan-rewrite"
              aria-label={editLabel}
              onClick={() => setEditing(true)}
            >
              <Pencil aria-hidden="true" />
              Rewrite this text
            </Button>
          </>
        )}
      </div>

      <div className="plan-why">
        <span className="plan-why-label">Why</span>
        <p className="plan-why-body">{why}</p>
      </div>
    </div>
  );
}

/**
 * The legend. ONCE per decision surface — never once per card.
 *
 * ALL THREE ITEMS ARE KEYS. The protected item used to be a SENTENCE — "Sealed chips are protected
 * facts — never altered" — set bold, in amber, running inline with "− removed" and "+ added". Two
 * keys and a warning: the row read as an alert banner rather than as a legend, and the one colour
 * this screen reserves for the protected family was spent on a line of prose.
 *
 * It is a key now, in the same shape as its neighbours: a glyph and its label. The glyph is the
 * lock the sealed chips themselves wear, and the promise is unchanged and unweakened — every chip
 * still carries "Protected fact — never altered" as its own title, and the compiler rules state it
 * in full.
 */
export function PlanDiffLegend() {
  return (
    <p className="plan-legend">
      <span className="plan-legend-del">− removed</span>
      <span className="plan-legend-ins">+ added</span>
      <span className="plan-legend-protected">
        <Lock aria-hidden="true" />
        protected · never altered
      </span>
    </p>
  );
}
